import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { services, exitReadiness } from "../data/content";
import { useReveal } from "../hooks/useReveal";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const allServices = [exitReadiness, ...services];
  const service = allServices.find((s) => s.slug === slug);
  useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-paper text-ink flex flex-col items-center justify-center px-6">
        <div className="serif text-5xl font-light mb-4">Not found</div>
        <p className="text-slate mb-8">That service page doesn&apos;t exist.</p>
        <button
          onClick={() => navigate("/")}
          data-testid="service-404-home"
          className="bg-ink text-paper px-6 py-3 text-sm hover:bg-moss transition-colors"
        >
          ← Back to home
        </button>
      </div>
    );
  }

  const otherServices = allServices.filter((s) => s.slug !== service.slug);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid={`service-page-${service.slug}`}>
      <Nav />

      <main className="pt-40 md:pt-48">
        {/* Header */}
        <section className="px-6 md:px-12 pb-20 md:pb-32">
          <div className="max-w-[1400px] mx-auto">
            <Link
              to="/#services"
              data-testid="service-back-link"
              className="overline text-slate hover:text-ink transition-colors inline-flex items-center gap-2 mb-12"
            >
              <span>←</span> All services
            </Link>

            <div className="grid grid-cols-12 gap-6 md:gap-10">
              <div className="col-span-12 md:col-span-2">
                <div className="serif text-6xl md:text-7xl font-light text-rule leading-none">
                  {service.n}
                </div>
              </div>
              <div className="col-span-12 md:col-span-9 lg:col-span-8">
                <h1
                  data-testid="service-title"
                  className="serif text-5xl sm:text-6xl md:text-7xl font-light tracking-tight leading-[1] text-ink"
                >
                  {service.title}
                </h1>
                <p className="mt-10 text-lg md:text-xl leading-relaxed text-slate max-w-2xl">
                  {service.deck}
                </p>
                <div className="mt-8 flex flex-wrap gap-2">
                  {service.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[11px] tracking-wider uppercase border border-rule px-3 py-1.5 text-slate"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Image */}
        <section className="px-6 md:px-12 mb-20 md:mb-32">
          <div className="max-w-[1400px] mx-auto">
            <div className="overflow-hidden reveal">
              <img
                src={service.image}
                alt={service.alt}
                className="w-full h-[320px] md:h-[560px] object-cover img-editorial"
                data-testid="service-hero-image"
              />
            </div>
          </div>
        </section>

        {/* Situation + Response framing */}
        <section className="px-6 md:px-12 mb-24 md:mb-32 border-y border-rule">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2">
            <div className="py-14 md:py-20 md:pr-12 md:border-r md:border-rule reveal">
              <div className="overline text-slate mb-4">The situation</div>
              <p className="serif text-2xl md:text-3xl font-light leading-snug text-slate">
                {service.situation}
              </p>
            </div>
            <div className="py-14 md:py-20 md:pl-12 reveal">
              <div className="overline text-olive mb-4">My response</div>
              <p className="serif text-2xl md:text-3xl font-light leading-snug text-ink">
                {service.response}
              </p>
            </div>
          </div>
        </section>

        {/* Long content */}
        <section className="px-6 md:px-12 mb-24 md:mb-32">
          <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-3">
              <div className="overline text-slate reveal">Detail</div>
            </div>
            <div className="col-span-12 md:col-span-8 lg:col-span-7 space-y-14 md:space-y-16">
              {service.longContent.map((block, idx) => (
                <article key={idx} className="reveal">
                  <h2 className="serif text-3xl md:text-4xl font-light leading-tight text-ink mb-5">
                    {block.heading}
                  </h2>
                  <p className="text-base md:text-lg leading-relaxed text-slate">
                    {block.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 md:px-12 py-20 md:py-32 bg-ink text-paper">
          <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-7">
              <div className="overline text-paper/60 mb-6 reveal">
                If this fits
              </div>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight reveal">
                Let&apos;s talk through whether this is
                <span className="italic text-paper/70"> the right engagement.</span>
              </h2>
              <p className="mt-8 text-base md:text-lg text-paper/70 max-w-lg reveal">
                A 30-minute call. No deck, no sales process.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
                <a
                  href="https://calendly.com/sarangandhi1/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-cta-calendly"
                  className="inline-flex items-center justify-center gap-3 bg-mustard text-ink px-7 py-4 text-sm tracking-wide hover:bg-paper hover:text-ink transition-colors duration-300 group"
                >
                  Book a 30-minute call
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
                <Link
                  to="/#contact"
                  data-testid="service-cta-form"
                  className="inline-flex items-center justify-center gap-3 text-sm tracking-wide text-paper hover:text-paper/70 transition-colors duration-300 px-2 py-4 border border-paper/30 hover:border-paper/50"
                >
                  Send a note instead
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Other services */}
        <section className="px-6 md:px-12 py-20 md:py-32 border-t border-rule">
          <div className="max-w-[1400px] mx-auto">
            <div className="overline text-slate mb-12 reveal">Other services</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule">
              {otherServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`related-service-${s.slug}`}
                  className="group bg-paper p-8 md:p-12 hover:bg-white transition-colors duration-300 reveal"
                >
                  <div className="overline text-slate mb-4">{s.n}</div>
                  <h3 className="serif text-2xl md:text-3xl font-light leading-tight text-ink group-hover:text-moss transition-colors">
                    {s.title}
                  </h3>
                  <div className="mt-8 overline text-ink inline-flex items-center gap-2">
                    Read more
                    <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
